import { FC, useCallback, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueNotificacaoConfig } from '../../../../hooks/estoque'
import { salvarEstoqueNotificacaoConfig } from '../../../../Service/estoque'
import { PapelNotificacao, PracaNotificacao } from '../../../../types/estoque'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'
import {
  BD, Button, CaixaLista, Card, Field, Folha, FolhaOverlay, FolhaPega, FolhaTopo, Input, ListaCartoes,
  PageSubtitle, PageTitle, PainelLateral, Spinner, StatusBadge, TabelaWrap, Table,
} from '../../styles'
import { useCelular } from '../../useCelular'
import { tratarErro } from '../erros'
import { plural } from '../formato'
import { useModal } from '../useModal'
import {
  PAPEL_DESCRICAO, PAPEL_ROTULO, PessoaDoEditor, REGRA_PADRAO, SituacaoDaPraca,
  alternar, avisosDoInscrito, impedimentoSalvar, mesmaLista, nomesConhecidos, pessoasDoEditor, situacaoDaPraca,
} from './regras'

interface Edicao {
  pracaId: number
  papel: PapelNotificacao
  nomes: Record<number, string>
}

const SELO: Record<SituacaoDaPraca, { texto: string; bg: string; cor: string }> = {
  CONFIGURADA: { texto: 'Configurada', bg: '#DCFCE7', cor: '#166534' },
  COM_AVISOS: { texto: 'Com avisos', bg: '#FEF3C7', cor: '#92400E' },
  REGRA_PADRAO: { texto: 'Regra padrão', bg: '#F3F4F6', cor: BD.grayText },
}

const detalheDaPessoa = (p: PessoaDoEditor): string => {
  if (p.inativa) return 'Usuário inativo: tire da lista'
  if (!p.apta) return 'Sem acesso a esta praça: tire da lista'
  const partes = [p.todasAsPracas ? 'Atende todas as praças' : 'Cadastrada nesta praça']
  if (p.semEmail) partes.push('sem e-mail: não vai receber')
  return partes.join(' · ')
}

export const EstoqueNotificacoes: FC = () => {
  const navigate = useNavigate()
  const celular = useCelular()
  const { data, isLoading, isError, isUnauthorized, mutate } = useEstoqueNotificacaoConfig()
  const [edicao, setEdicao] = useState<Edicao | null>(null)
  const [marcados, setMarcados] = useState<number[]>([])
  const [busca, setBusca] = useState('')
  const [busy, setBusy] = useState(false)
  const painel = useRef<HTMLDivElement>(null)
  const folha = useRef<HTMLDivElement>(null)

  const papeis = data?.papeis ?? []
  const pracas = data?.pracas ?? []
  const pessoas = data?.pessoas ?? []
  const pracaEditada = edicao ? pracas.find((p) => p.pracaId === edicao.pracaId) ?? null : null
  const papelEditado = edicao && pracaEditada ? pracaEditada.papeis.find((p) => p.papel === edicao.papel) ?? null : null
  const temRascunho = !!papelEditado && !mesmaLista(marcados, papelEditado.inscritos.map((i) => i.id))

  const descartar = useCallback(() => {
    if (!busy) setEdicao(null)
  }, [busy])
  const fecharSemRascunho = useCallback(() => {
    if (!busy && !temRascunho) setEdicao(null)
  }, [busy, temRascunho])
  useModal(!!papelEditado, [painel, folha], fecharSemRascunho)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const abrir = (praca: PracaNotificacao, papel: PapelNotificacao) => {
    const inscritos = praca.papeis.find((p) => p.papel === papel)?.inscritos ?? []
    setEdicao({ pracaId: praca.pracaId, papel, nomes: nomesConhecidos(pessoas, inscritos) })
    setMarcados(inscritos.map((i) => i.id))
    setBusca('')
  }

  const salvar = async () => {
    if (!edicao || !pracaEditada) return
    setBusy(true)
    try {
      await salvarEstoqueNotificacaoConfig({ pracaId: edicao.pracaId, papel: edicao.papel, usuarioIds: marcados })
      toast.success(`Notificações de ${pracaEditada.nome} salvas`)
      setEdicao(null)
      await mutate()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      await mutate().catch(() => undefined)
    } finally {
      setBusy(false)
    }
  }

  const selo = (praca: PracaNotificacao) => {
    const s = SELO[situacaoDaPraca(praca)]
    return <StatusBadge $bg={s.bg} $color={s.cor}>{s.texto}</StatusBadge>
  }

  const inscritosDoPapel = (praca: PracaNotificacao, papel: PapelNotificacao) => {
    const doPapel = praca.papeis.find((p) => p.papel === papel)
    if (!doPapel || doPapel.inscritos.length === 0) {
      return <span style={{ fontSize: 13, color: BD.grayText }}>Ninguém configurado</span>
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {doPapel.inscritos.map((i) => {
          const avisos = avisosDoInscrito(i)
          return (
            <StatusBadge key={i.id} $bg={avisos.length ? '#FEF3C7' : '#F3F4F6'} $color={avisos.length ? '#92400E' : BD.black}>
              {i.nome}{avisos.length ? ` · ${avisos.join(' · ')}` : ''}
            </StatusBadge>
          )
        })}
        {doPapel.usaRegraPadrao && (
          <span style={{ fontSize: 12, color: BD.grayText }}>ninguém da lista recebe: vale a regra padrão</span>
        )}
      </div>
    )
  }

  const botaoEditar = (praca: PracaNotificacao, papel: PapelNotificacao, largo = false) => (
    <Button key={papel} $variant="secondary" style={largo ? { width: '100%' } : undefined}
            disabled={busy} onClick={() => abrir(praca, papel)}>
      {papeis.length > 1 ? `Editar ${PAPEL_ROTULO[papel].toLowerCase()}` : 'Editar quem recebe'}
    </Button>
  )

  const editor = () => {
    if (!data || !edicao || !pracaEditada || !papelEditado) return null
    const lista = pessoasDoEditor({
      pessoas, aptos: papelEditado.elegiveis, inscritos: papelEditado.inscritos, marcados, busca, nomes: edicao.nomes,
    })
    const naoMarcados = lista.filter((p) => !p.marcada).length
    const impedimento = impedimentoSalvar(
      marcados, papelEditado.inscritos.map((i) => i.id), papelEditado.elegiveis, data.maxInscritos)
    const titulo = papeis.length > 1
      ? `${pracaEditada.nome} · ${PAPEL_ROTULO[edicao.papel]}`
      : `Quem recebe de ${pracaEditada.nome}`
    const conteudo = (
      <>
        <FolhaTopo>
          <h2>{titulo}</h2>
        </FolhaTopo>
        <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 12 }}>
          {PAPEL_DESCRICAO[edicao.papel]} {REGRA_PADRAO}
        </div>
        <Field>
          <label>Buscar pessoa</label>
          <Input aria-label="Buscar pessoa" value={busca} placeholder="Nome" onChange={(e) => setBusca(e.target.value)} />
        </Field>
        <div style={{ fontSize: 12, color: BD.grayText, margin: '10px 0 8px' }}>
          {plural(marcados.length, 'pessoa marcada', 'pessoas marcadas')}
        </div>
        <div role="group" aria-label={titulo} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {lista.map((p) => (
            <button key={p.id} type="button" aria-pressed={p.marcada} disabled={busy}
                    onClick={() => setMarcados((m) => alternar(m, p.id))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44,
                      padding: '10px 12px', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer',
                      borderRadius: 10, border: `1px solid ${p.marcada ? BD.black : BD.grayBorder}`,
                      background: p.marcada ? '#F3F4F6' : '#fff',
                    }}>
              <span aria-hidden style={{
                flexShrink: 0, width: 18, height: 18, borderRadius: 4, fontSize: 12, lineHeight: '14px',
                textAlign: 'center', color: '#fff', border: `2px solid ${p.marcada ? BD.black : BD.grayBorder}`,
                background: p.marcada ? BD.black : '#fff',
              }}>
                {p.marcada ? '✓' : ''}
              </span>
              <span style={{ flexGrow: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: BD.black }}>{p.nome}</span>
                <span style={{ display: 'block', fontSize: 12, color: p.apta ? BD.grayText : BD.red }}>{detalheDaPessoa(p)}</span>
              </span>
            </button>
          ))}
          {naoMarcados === 0 && (
            <div style={{ fontSize: 13, color: BD.grayText }}>
              {busca.trim()
                ? 'Ninguém mais com esse nome.'
                : lista.length ? 'Todas as pessoas que alcançam esta praça já estão marcadas.' : 'Ninguém com a permissão Atender alcança esta praça.'}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button disabled={busy || !!impedimento} onClick={salvar}>{busy ? 'Salvando…' : 'Salvar'}</Button>
          <Button $variant="secondary" disabled={busy} onClick={descartar}>Descartar alterações</Button>
          {impedimento && <span style={{ fontSize: 13, color: BD.grayText }}>{impedimento}</span>}
        </div>
      </>
    )
    return (
      <>
        <FolhaOverlay $open onClick={fecharSemRascunho} />
        {celular ? (
          <Folha ref={folha} tabIndex={-1} $open role="dialog" aria-modal="true" aria-label={titulo}>
            <FolhaPega />
            {conteudo}
          </Folha>
        ) : (
          <PainelLateral ref={painel} tabIndex={-1} role="dialog" aria-modal="true" aria-label={titulo}>
            {conteudo}
          </PainelLateral>
        )}
      </>
    )
  }

  const erro = (
    <>
      Não foi possível carregar as notificações.{' '}
      <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
    </>
  )
  const colunas = papeis.length + 3

  return (
    <>
      <PageTitle>Notificações</PageTitle>
      <PageSubtitle>
        Quem recebe o e-mail de cada praça. Uma praça pode ter várias pessoas, e a mesma pessoa pode responder por várias
        praças. A resposta e o cancelamento de um pedido já assumido vão para quem assumiu. {REGRA_PADRAO}
      </PageSubtitle>

      <CaixaLista>
        <TabelaWrap>
          <Table>
            <thead>
              <tr>
                <th>Praça</th>
                {papeis.map((p) => <th key={p}>{PAPEL_ROTULO[p]}</th>)}
                <th>Situação</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={colunas} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && (
                <tr><td colSpan={colunas} style={{ textAlign: 'center', padding: 24, color: BD.red }}>{erro}</td></tr>
              )}
              {!isLoading && !isError && pracas.length === 0 && (
                <tr><td colSpan={colunas} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>Nenhuma praça ativa no estoque.</td></tr>
              )}
              {!isLoading && !isError && pracas.map((praca) => (
                <tr key={praca.pracaId}>
                  <td><b>{praca.nome}</b></td>
                  {papeis.map((papel) => <td key={papel}>{inscritosDoPapel(praca, papel)}</td>)}
                  <td>{selo(praca)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{papeis.map((papel) => botaoEditar(praca, papel))}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && <Card style={{ padding: 20, color: BD.red }}>{erro}</Card>}
          {!isLoading && !isError && pracas.length === 0 && (
            <Card style={{ padding: 20, color: BD.grayText }}>Nenhuma praça ativa no estoque.</Card>
          )}
          {!isLoading && !isError && pracas.map((praca) => (
            <Card key={praca.pracaId} style={{ padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flexGrow: 1, fontSize: 16, fontWeight: 600 }}>{praca.nome}</span>
                {selo(praca)}
              </div>
              {papeis.map((papel) => (
                <div key={papel} style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${BD.grayBorder}` }}>
                  {papeis.length > 1 && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{PAPEL_ROTULO[papel]}</div>}
                  {inscritosDoPapel(praca, papel)}
                  <div style={{ marginTop: 10 }}>{botaoEditar(praca, papel, true)}</div>
                </div>
              ))}
            </Card>
          ))}
        </ListaCartoes>
      </CaixaLista>

      {editor()}
    </>
  )
}

export default EstoqueNotificacoes
